import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DatePipe, NgIf } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatError,
  MatFormField,
  MatInput,
  MatLabel,
} from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperNext,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, Subscription } from 'rxjs';
import { PersonalInfoComponent } from '../../components/personal_info/personal-info.component';
import { AuthStateService } from '../../core/services/auth-state.service';
import { AuthService } from '../../core/services/auth.service';
import { SessionExpiredError } from '../../core/services/session-expired.error';
import {
  PRIVACY_POLICY,
  UserForm,
  UserFormField,
  UserFormFieldPreview,
  UserFormSection,
  WORKING_EXPERIENCE,
} from '../../shared/models/userForm/user-form.model.form';
import { DialogService } from '../../shared/services/dialog.service';
import { FormCompletedError } from '../../shared/services/form-completed.error';
import { FormService } from '../../shared/services/form.service';

@Component({
  selector: 'admin-page',
  standalone: true,
  imports: [
    MatStepper,
    MatStep,
    MatStepperNext,
    MatButton,
    MatStepperPrevious,
    MatStepLabel,
    TranslatePipe,
    MatCheckbox,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    PersonalInfoComponent,
    MatDivider,
    MatProgressSpinnerModule,
    DatePipe,
    MatIcon,
    MatIconButton,
    NgIf,
  ],
  templateUrl: './user-page.component.html',
})
export class UserPageComponent implements OnInit, OnDestroy {
  private readonly authStateService = inject(AuthStateService);
  private readonly authService = inject(AuthService);
  private readonly formService = inject(FormService);

  public isUpdatingForm: boolean = false;

  public submissionCompleted: boolean = false;
  public isSubmitting: boolean = false;
  public isFormCompleted: boolean = false;
  protected readonly sessionExpired = signal(false);

  protected readonly currentUser = this.authStateService.user();
  privacyForm: FormGroup = this.formService.buildUserForm(PRIVACY_POLICY);
  workingExperienceForm: FormGroup =
    this.formService.buildUserForm(WORKING_EXPERIENCE);

  public stepIndexes = {
    PRIVACY_POLICY: 0,
    WORKING_EXPERIENCE: 1,
    SUBMIT_FORM: 2,
  };

  constructor(private dialogService: DialogService) {}

  protected get isFormLocked(): boolean {
    return this.isFormCompleted || this.submissionCompleted;
  }

  protected isProcedureValid = computed(() => {
    if (this.currentUser?.startDate && this.currentUser?.endDate) {
      const startDateFromISO = new Date(this.currentUser?.startDate);
      const endDateFromISO = new Date(this.currentUser?.endDate);
      const now = new Date();

      if (
        isNaN(startDateFromISO.getTime()) ||
        isNaN(endDateFromISO.getTime())
      ) {
        return false;
      }

      return now >= startDateFromISO && now < endDateFromISO;
    }
    return false;
  });

  protected workingYears = signal<number | null>(null);
  workingYearsRange = computed(() => {
    const endYear = this.currentUser?.workingYear! - 1;
    const startYear = endYear - this.workingYears()!;
    const range: number[] = [];
    for (let i = startYear + 1; i <= endYear; i++) {
      range.push(i);
    }
    return range;
  });

  protected areNotesVisible = signal<boolean[]>([false, false, false]);

  protected activePreviewLabel = signal<string>('');
  protected activePreviewValue = signal<string>('');
  protected hasPendingServiceChange = signal(false);
  protected readonly loadingSteps = signal<Set<number>>(new Set());
  protected readonly loadErrors = signal<Map<number, boolean>>(new Map());

  private readonly previewSources = new Map<string, UserFormFieldPreview[]>();
  private readonly notesMaxLengths = new Map<string, number>();
  private readonly loadedSteps = new Set<number>();
  private readonly answerIds = new Map<number, string>();
  private pendingServiceChanges = new Set<string>();
  private previewValueSub: Subscription | null = null;
  private skipNextStepSave = false;
  private ignoredSelectionIndex: number | null = null;

  ngOnInit() {
    if (this.currentUser) {
      this.workingYears.set(this.formService.getWorkingYears(this.currentUser));
      const workingExperienceTemplate =
        this.fillWithActivities(WORKING_EXPERIENCE);
      this.configureFieldConfigurations(workingExperienceTemplate);
      this.workingExperienceForm = this.formService.buildUserForm(
        workingExperienceTemplate,
      );
      this.configurePendingServiceChangeValidation();
      this.configureStepLoadingValidation(this.stepIndexes.PRIVACY_POLICY);
      this.configureStepLoadingValidation(this.stepIndexes.WORKING_EXPERIENCE);
      this.loadStep(this.stepIndexes.PRIVACY_POLICY);
    }
  }

  private fillWithActivities(formTemplate: UserForm): UserForm {
    return {
      ...formTemplate,
      forms: [...formTemplate.forms, this.createActivitiesForm()],
    };
  }

  public onStepChange(event: StepperSelectionEvent, stepper: MatStepper) {
    if (
      this.isFormLocked ||
      this.isUpdatingForm ||
      this.isSubmitting ||
      this.sessionExpired()
    ) {
      this.ignoredSelectionIndex = event.previouslySelectedIndex;
      queueMicrotask(() => {
        stepper.selectedIndex = event.previouslySelectedIndex;
      });
      return;
    }

    if (this.ignoredSelectionIndex === event.selectedIndex) {
      this.ignoredSelectionIndex = null;
      return;
    }

    if (
      event.previouslySelectedIndex === this.stepIndexes.WORKING_EXPERIENCE &&
      event.selectedIndex !== this.stepIndexes.WORKING_EXPERIENCE &&
      this.hasPendingServiceChange()
    ) {
      this.ignoredSelectionIndex = this.stepIndexes.WORKING_EXPERIENCE;
      queueMicrotask(() => {
        stepper.selectedIndex = this.stepIndexes.WORKING_EXPERIENCE;
      });
      return;
    }

    if (this.skipNextStepSave) {
      this.skipNextStepSave = false;
    } else {
      this.saveStep(event.previouslySelectedIndex);
    }
    this.loadStep(event.selectedIndex);
  }

  private getFormForStep(index: number): FormGroup | null {
    switch (index) {
      case this.stepIndexes.PRIVACY_POLICY:
        return this.privacyForm;
      case this.stepIndexes.WORKING_EXPERIENCE:
        return this.workingExperienceForm;
      default:
        return null;
    }
  }

  protected isStepLoading(index: number): boolean {
    return this.loadingSteps().has(index);
  }

  protected hasLoadError(index: number): boolean {
    return this.loadErrors().has(index);
  }

  protected retryLoadStep(index: number) {
    if (this.isFormLocked) {
      return;
    }
    this.loadStep(index, true);
  }

  private loadStep(index: number, retry = false) {
    if (
      this.isFormLocked ||
      this.getFormForStep(index) === null ||
      this.isStepLoading(index)
    ) {
      return;
    }
    if (this.loadedSteps.has(index) && !retry) {
      return;
    }

    this.setStepLoading(index, true);
    this.setLoadError(index, false);
    this.formService.getFormState(index).subscribe({
      next: (result) => {
        if (result?.status && result.status >= 400) {
          this.setStepLoading(index, false);
          this.setLoadError(index, true);
          return;
        }
        const form = this.getFormForStep(index);
        if (form) {
          this.processFormData(result, form, index);
          this.isFormCompleted ||= Boolean(result?.completed);
          this.loadedSteps.add(index);
        }
        this.setStepLoading(index, false);
      },
      error: (error) => {
        this.setStepLoading(index, false);
        this.setLoadError(index, true);
        this.handleSessionError(error);
      },
    });
  }

  private processFormData(result: any, form: FormGroup, index: number) {
    const savedAnswers = result?.questions;
    if (savedAnswers?.answers_id) {
      this.answerIds.set(index, savedAnswers.answers_id);
    }

    const formsArray = form.get('forms') as FormArray;
    this.patchSavedForms(savedAnswers?.forms, formsArray);
  }

  private setStepLoading(index: number, isLoading: boolean) {
    const loadingSteps = new Set(this.loadingSteps());
    isLoading ? loadingSteps.add(index) : loadingSteps.delete(index);
    this.loadingSteps.set(loadingSteps);
    this.getFormForStep(index)?.updateValueAndValidity();
  }

  private setLoadError(index: number, hasError: boolean) {
    const loadErrors = new Map(this.loadErrors());
    hasError ? loadErrors.set(index, true) : loadErrors.delete(index);
    this.loadErrors.set(loadErrors);
  }

  private patchSavedForms(
    savedForms: any[] | undefined,
    formsArray: FormArray,
  ) {
    (savedForms ?? []).forEach((savedForm: any) => {
      const formGroup = this.findFormGroup(savedForm, formsArray);

      if (!formGroup) {
        return;
      }

      this.patchSavedFieldValues(savedForm.fields, formGroup);
      formGroup.patchValue({
        form_name: savedForm.form_name,
        section_name: savedForm.section_name,
        page_name: savedForm.page_name,
      });
    });
  }

  /**
   * The form is built from the logged-in user's current level. Use that
   * runtime structure as the allowlist when overwriting a saved draft, so a
   * field left over from a previous level can never be written back to the
   * answer document.
   */
  private getSavePayload(form: FormGroup): any {
    const values = form.getRawValue();
    const formsArray = this.getForms(form);

    return {
      ...values,
      forms: (values.forms ?? []).flatMap((savedForm: any) => {
        const formGroup = this.findFormGroup(savedForm, formsArray);
        if (!formGroup) {
          return [];
        }

        const currentFields = new Map(
          this.getFields(formGroup).controls.map((field) => [
            field.get('name')?.value,
            field,
          ]),
        );

        return [
          {
            ...savedForm,
            fields: (savedForm.fields ?? []).flatMap((field: any) => {
              const currentField = currentFields.get(field.name);
              if (!currentField) {
                return [];
              }

              return [this.normalizeFieldForSave(field, currentField)];
            }),
          },
        ];
      }),
    };
  }

  private findFormGroup(
    savedForm: any,
    formsArray: FormArray,
  ): FormGroup | undefined {
    return formsArray.controls.find(
      (control) =>
        (savedForm.form_name &&
          control.get('form_name')?.value === savedForm.form_name) ||
        (savedForm.section_name &&
          control.get('section_name')?.value === savedForm.section_name),
    ) as FormGroup | undefined;
  }

  private patchSavedFieldValues(
    savedFields: any[] | undefined,
    formGroupToPatch: FormGroup,
  ) {
    const fieldsArray = formGroupToPatch.get('fields') as FormArray;

    (savedFields ?? []).forEach((savedField: any) => {
      const fieldGroup = fieldsArray.controls.find(
        (control) => control.get('name')?.value === savedField.name,
      );

      if (!fieldGroup) {
        return;
      }

      if (this.isReadOnlySeniorityDate(fieldGroup)) {
        return;
      }

      fieldGroup.get('value')?.patchValue(savedField.value ?? '');
      fieldGroup.get('notes')?.patchValue({
        disabled: savedField.notes?.disabled ?? true,
        text: savedField.notes?.text ?? '',
      });
    });
  }

  private normalizeFieldForSave(
    field: any,
    currentField: AbstractControl,
  ): any {
    if (!this.isReadOnlySeniorityDate(currentField)) {
      return field;
    }

    return {
      ...field,
      value: this.formService.fillExperienceForm(field.name),
      notes: {
        disabled: true,
        text: '',
      },
    };
  }

  private isReadOnlySeniorityDate(field: AbstractControl): boolean {
    return (
      field.get('can_edit')?.value === false &&
      this.formService.isSeniorityDateField(field.get('name')?.value)
    );
  }

  private createActivitiesForm(): UserFormSection {
    const isSingleNote = this.formService.isSingleNoteFieldUser(
      this.currentUser,
    );

    return {
      form_name: 'activity-form',
      section_name: 'activities',
      page_name: 'performance',
      fields: this.workingYearsRange()
        .reverse()
        .map((e) =>
          this.generateFormField(
            e,
            isSingleNote ? 'Note' : null,
            !isSingleNote,
          ),
        ),
    };
  }

  private generateFormField(
    year: number,
    labelPrefix: string | null,
    required: boolean,
  ): UserFormField {
    return {
      id: `activity-${year}`,
      name: `attivita-${year}`,
      label: `${labelPrefix ?? 'Attività'} ${year}`,
      type: 'text',
      disabled: false,
      notes: {
        disabled: true,
        text: '',
      },
      canEdit: false,
      required,
      visible: null,
      value: null,
      preview: [{ source: 'value' }],
    };
  }

  updateForm(form: FormGroup, stepper?: MatStepper) {
    if (
      this.isFormLocked ||
      form.invalid ||
      (form === this.workingExperienceForm && this.hasPendingServiceChange())
    ) {
      form.markAllAsTouched();
      return;
    }

    this.saveStep(form.value.index as number, stepper);
  }

  private saveStep(index: number, stepper?: MatStepper) {
    const form = this.getFormForStep(index);
    if (
      !form ||
      this.isStepLoading(index) ||
      this.isUpdatingForm ||
      this.isSubmitting ||
      this.isFormLocked ||
      this.sessionExpired()
    ) {
      return;
    }
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }
    if (form === this.workingExperienceForm && this.hasPendingServiceChange()) {
      return;
    }

    this.isUpdatingForm = true;
    this.formService
      .updateForm(this.getSavePayload(form), this.answerIds.get(index))
      .pipe(finalize(() => (this.isUpdatingForm = false)))
      .subscribe({
        next: (answerId) => {
          if (!answerId) {
            return;
          }
          this.answerIds.set(index, answerId);

          if (stepper) {
            this.skipNextStepSave = true;
            // `finalize` must reset the operation flag before the stepper emits
            // its synchronous selection change, otherwise the navigation guard
            // correctly treats this successful advance as a duplicate action.
            queueMicrotask(() => stepper.next());
          }
        },
        error: (error) => this.handleFormStateError(error),
      });
  }

  submitForm(form: FormGroup) {
    if (
      this.isSubmitting ||
      this.isUpdatingForm ||
      this.isFormLocked ||
      this.sessionExpired() ||
      form.invalid
    ) {
      form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.formService
      .submitForm(form.getRawValue())
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (success) => {
          this.submissionCompleted = success;

          if (this.submissionCompleted) {
            console.log('Submission was successful!');
          } else {
            console.log('Submission failed.');
          }
        },
        error: (error) => {
          this.submissionCompleted = false;
          this.handleFormStateError(error);
        },
      });
  }

  protected async loginAgain(): Promise<void> {
    try {
      await this.authService.login();
      this.sessionExpired.set(false);
    } catch (error) {
      this.handleSessionError(error);
    }
  }

  private handleSessionError(error: unknown): void {
    if (error instanceof SessionExpiredError) {
      this.sessionExpired.set(true);
    }
  }

  private handleFormStateError(error: unknown): void {
    if (error instanceof FormCompletedError) {
      this.isFormCompleted = true;
      return;
    }
    this.handleSessionError(error);
  }

  protected getForms(group: AbstractControl): FormArray {
    return group.get('forms') as FormArray;
  }

  protected getFields(group: AbstractControl): FormArray {
    return group.get('fields') as FormArray;
  }

  private configureFieldConfigurations(form: UserForm) {
    this.previewSources.clear();
    this.notesMaxLengths.clear();
    form.forms.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.preview?.length) {
          this.previewSources.set(field.name, field.preview);
        }
        if (field.notes.maxLength) {
          this.notesMaxLengths.set(field.name, field.notes.maxLength);
        }
      });
    });
  }

  protected getNotesMaxLength(fieldName?: string): number | undefined {
    return fieldName ? this.notesMaxLengths.get(fieldName) : undefined;
  }

  protected openPreview(
    field: { name?: string; label?: string },
    source: UserFormFieldPreview['source'],
    control: AbstractControl,
  ) {
    const preview = field.name
      ? this.previewSources
          .get(field.name)
          ?.find((item) => item.source === source)
      : undefined;

    if (!preview) {
      this.closePreview();
      return;
    }

    this.activePreviewLabel.set(preview.label ?? field.label ?? '');
    this.activePreviewValue.set(control.value || '');

    this.previewValueSub?.unsubscribe();
    this.previewValueSub = control.valueChanges.subscribe((value) => {
      this.activePreviewValue.set(value || '');
    });
  }

  protected onPreviewAreaBlur(event: FocusEvent) {
    const wrapper = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as HTMLElement | null;
    if (!related || !wrapper.contains(related)) {
      this.closePreview();
    }
  }

  protected getSeniorityDateValidationMessage(
    field: AbstractControl,
  ): string | null {
    if (!this.formService.isSeniorityDateField(field.get('name')?.value)) {
      return null;
    }

    const value = field.get('value')?.value;
    if (!value) {
      return field.disabled ? 'USER_PAGE.VALIDATION.REQUIRED' : null;
    }

    return this.formService.isValidSeniorityDate(value)
      ? null
      : 'USER_PAGE.VALIDATION.DATE_FORMAT';
  }

  private closePreview() {
    this.previewValueSub?.unsubscribe();
    this.previewValueSub = null;
    this.activePreviewLabel.set('');
    this.activePreviewValue.set('');
  }

  ngOnDestroy() {
    this.previewValueSub?.unsubscribe();
  }

  protected openNotes(
    fieldIndex: number,
    fieldName: string,
    dateInput?: HTMLInputElement,
  ) {
    const updatedVisibility = [...this.areNotesVisible()];
    updatedVisibility[fieldIndex] = true;
    this.areNotesVisible.set(updatedVisibility);

    const formsArray = this.getForms(this.workingExperienceForm);
    let wasEnabled = false;

    formsArray.controls.forEach((formGroup: AbstractControl) => {
      const fieldsArray = this.getFields(formGroup);

      fieldsArray.controls.forEach((fieldGroup: AbstractControl) => {
        if (
          fieldGroup instanceof FormGroup &&
          fieldGroup.get('name')?.value === fieldName
        ) {
          fieldGroup.enable();

          fieldGroup.get('notes.disabled')?.patchValue(false);
          this.setPendingServiceChange(fieldName, true);
          wasEnabled = true;

          return;
        }
      });
    });

    if (wasEnabled) {
      dateInput?.focus();
      dateInput?.setSelectionRange(0, 0);
    }
  }

  protected patchNotes(fieldIndex: number, fieldName: string) {
    const formsArray = this.getForms(this.workingExperienceForm);

    formsArray.controls.forEach((formGroup: AbstractControl) => {
      const fieldsArray = this.getFields(formGroup);

      fieldsArray.controls.forEach((fieldGroup: AbstractControl) => {
        if (fieldGroup.get('name')?.value === fieldName) {
          const inputValue = fieldGroup.get('notes.text')?.value;

          fieldGroup.patchValue({
            notes: {
              disabled: false,
              text: inputValue,
            },
          });

          fieldGroup.disable();
        }
      });
    });

    const updatedVisibility = [...this.areNotesVisible()];
    updatedVisibility[fieldIndex] = false;
    this.areNotesVisible.set(updatedVisibility);
    this.setPendingServiceChange(fieldName, false);
  }

  protected restoreValue(fieldIndex: number, fieldName: string) {
    const originalValue = this.formService.fillExperienceForm(fieldName);

    if (!originalValue) {
      console.warn(`No original value found for field: ${fieldName}`);
      return;
    }

    const formsArray = this.getForms(this.workingExperienceForm);

    formsArray.controls.forEach((formGroup: AbstractControl) => {
      const fieldsArray = this.getFields(formGroup);

      fieldsArray.controls.forEach((fieldGroup: AbstractControl) => {
        if (fieldGroup.get('name')?.value === fieldName) {
          // Clear notes text and restore original value
          fieldGroup.patchValue({
            notes: {
              disabled: true,
              text: '',
            },
            value: originalValue,
          });

          fieldGroup.disable();
        }
      });
    });

    const updatedVisibility = [...this.areNotesVisible()];
    updatedVisibility[fieldIndex] = false;
    this.areNotesVisible.set(updatedVisibility);
    this.setPendingServiceChange(fieldName, false);
  }

  private setPendingServiceChange(fieldName: string, isPending: boolean) {
    this.pendingServiceChanges ??= new Set<string>();
    isPending
      ? this.pendingServiceChanges.add(fieldName)
      : this.pendingServiceChanges.delete(fieldName);
    this.hasPendingServiceChange.set(this.pendingServiceChanges.size > 0);
    this.workingExperienceForm.updateValueAndValidity();
  }

  private configurePendingServiceChangeValidation() {
    this.workingExperienceForm.addValidators(() =>
      this.hasPendingServiceChange() ? { pendingServiceChange: true } : null,
    );
    this.workingExperienceForm.updateValueAndValidity();
  }

  private configureStepLoadingValidation(index: number) {
    const form = this.getFormForStep(index);
    form?.addValidators(() =>
      this.isStepLoading(index) ? { stepLoading: true } : null,
    );
    form?.updateValueAndValidity();
  }

  protected readonly open = open;
}
